<script setup lang="ts">
import InteractiveTopics from "~/components/InteractiveTopics.vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import QuizEngine from "~/features/computer/components/QuizEngine.vue";
import { operatorsExercises } from "~/features/computer/data/exercises/operators.exercises";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { ch2Questions } from "~/features/python/ch2/exercise/data/ch2.questions";
import { pythonInteractiveCards } from "~/features/python/data/chapter2-interactive";

const relatedCards = pythonInteractiveCards.filter((item) => item.to !== "/11th/computer/python/operators/");
const operatorProgramQuestions = ch2Questions.filter(
  (question) => question.topic === "operators" || question.concepts.includes("operators") || question.concepts.includes("expressions"),
);

usePageSeo({
  title: "Chapter 2 Python Operators | Ustaad",
  description: "Chapter 2 Python operators with solved MCQs, short answers, expression examples and board-style questions for class 11 students.",
  keywords: "chapter 2 python operators, python operators class 11, expressions chapter 2 python, first year computer python operators",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Ch 2 Python", item: "/11th/computer/python/" },
    { name: "Operators", item: "/11th/computer/python/operators/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "Chapter 2 Python Operators",
    items: [
      { name: "Chapter 2 Python", path: "/11th/computer/python/" },
      { name: "Chapter 2 Python MCQs", path: "/11th/computer/python/mcqs/" },
      { name: "Chapter 2 Python Short Questions", path: "/11th/computer/python/short-questions/" },
      { name: "Chapter 2 Python Programs", path: "/11th/computer/python/programs/" },
      { name: "Chapter 2 Python Loops", path: "/11th/computer/python/loops/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-3xl bg-slate-950 p-6 text-white">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">Python Chapter 2</p>
        <h1 class="mt-2 text-2xl font-bold tracking-tight">Chapter 2 Python Operators</h1>
        <p class="mt-3 text-sm leading-7 text-slate-200">
          Arithmetic, comparison aur expression-based questions ko step by step samjhein. Yeh topic MCQs aur short answers dono me bohat kaam aata hai.
        </p>
      </section>

      <section class="grid gap-6 lg:grid-cols-2">
        <article class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Exam shortcut</h2>
          <div class="mt-4 space-y-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p>- `+`, `-`, `*`, `/`, `%` aur `//` ka basic role yaad rakhein</p>
            <p>- `==`, `>`, `<` comparison operator ka result True ya False hota hai</p>
            <p>- Expression solve karte waqt multiplication aur division pehle hoti hai</p>
          </div>
        </article>

        <article class="rounded-3xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-950/20">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">What to prepare</h2>
          <div class="mt-4 space-y-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p>- Arithmetic operators aur examples</p>
            <p>- Comparison operators aur boolean result</p>
            <p>- Expression evaluation step by step</p>
          </div>
        </article>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Operators MCQs</h2>
          <p class="text-sm text-slate-600 dark:text-slate-300">Pehle objective concepts clear karein, phir expression aur program questions dekhein.</p>
        </div>
        <div class="mt-4">
          <QuizEngine :questions="operatorsExercises.mcqs" />
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Important Short Answers</h2>
          <p class="text-sm text-slate-600 dark:text-slate-300">Yeh short questions exam me direct help karte hain.</p>
        </div>
        <div class="mt-4 grid gap-3">
          <article
            v-for="item in operatorsExercises.short"
            :key="item.id"
            class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800"
          >
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white">{{ item.question }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div class="space-y-1">
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Board-style Operator Questions</h2>
          <p class="text-sm text-slate-600 dark:text-slate-300">Expression aur operator-based practical questions yahan se kholo.</p>
        </div>

        <div class="mt-4 grid gap-3">
          <NuxtLink
            v-for="question in operatorProgramQuestions"
            :key="question.id"
            :to="`/11th/computer/python/programs/#q-${question.id}`"
            class="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-sky-300 hover:bg-sky-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            <div class="flex flex-wrap items-center justify-between gap-3">
              <h3 class="text-sm font-semibold text-slate-900 dark:text-white">Q{{ question.number }}. {{ question.question }}</h3>
              <span class="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                {{ question.type }}
              </span>
            </div>
          </NuxtLink>
        </div>
      </section>

      <InteractiveTopics
        eyebrow="More Python Topics"
        title="More Python Topics"
        intro="Operators ke baad baqi important Python topics bhi yahan se khol sakte hain."
        :cards="relatedCards"
      />
    </div>
  </PageContainer>
</template>
